/** Presentation helpers shared by the ski-area and lift tables. */
import {
  flexRender,
  type CellContext,
  type Column,
  type HeaderContext,
  type Row,
  type RowData,
  type Table,
} from "@tanstack/react-table";
import {
  type CSSProperties,
  type ChangeEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  countryCodeToFlag,
  isNumericRange,
  type NumericRange,
  rangeContains,
  searchKey,
} from "./filters";
import { formatBound, formatNumber, MISSING_VALUE } from "./formatters";
import {
  boundsFromBins,
  buildHistogram,
  describeBin,
  describeBounds,
  type Histogram,
  overlapsBin,
  parseBound,
  restricts,
  UNBOUNDED,
  withBound,
} from "./range";
import { columnLabel, type TableFeatures } from "./table-features";
import type { UrlValueCodec } from "./url-state";

function HeaderLabel({
  description,
  focusable = true,
  label,
}: {
  description?: string;
  focusable?: boolean;
  label: ReactNode;
}) {
  if (!description) {
    return label;
  }
  return (
    <span className="oss-table-tooltip-trigger" tabIndex={focusable ? 0 : undefined}>
      {label}
      <span className="oss-table-tooltip" role="tooltip">
        {description}
      </span>
    </span>
  );
}

/**
 * Decorate a plain-text leaf heading with sorting and a tooltip at render time.
 *
 * Column definitions keep `header` as a string so the column picker and the
 * URL state can reuse the same label; group and unlabeled headings render as is.
 */
export function renderColumnHeader<TData extends RowData>(
  context: HeaderContext<TableFeatures, TData, unknown>,
  description?: string,
): ReactNode {
  const { column } = context;
  const label = columnLabel(column.columnDef);
  if (label === undefined || column.columns.length > 0) {
    return flexRender(column.columnDef.header, context);
  }
  const tooltip = column.columnDef.meta?.description ?? description;
  if (!column.getCanSort()) {
    return <HeaderLabel description={tooltip} label={label} />;
  }
  return (
    <button
      aria-label={`Sort by ${column.id}`}
      className="oss-table-sort-button"
      onClick={column.getToggleSortingHandler()}
      type="button"
    >
      <HeaderLabel description={tooltip} focusable={false} label={label} />
      {{ asc: "▲", desc: "▼" }[column.getIsSorted() as string] ?? null}
    </button>
  );
}

export function DebouncedInput({
  ariaLabel,
  className,
  inputMode,
  onChange,
  placeholder,
  value: externalValue,
}: {
  ariaLabel: string;
  className: string;
  inputMode?: "decimal";
  onChange: (value: string) => void;
  placeholder?: string;
  value: unknown;
}) {
  const [value, setValue] = useState(String(externalValue ?? ""));
  // Only a value the visitor typed is worth reporting. Reporting on mount, or
  // whenever `onChange` is rebuilt, made two of these inputs overwrite each
  // other's bound with a stale one on a loop.
  const typed = useRef(false);
  const latest = useRef(onChange);
  latest.current = onChange;

  useEffect(() => {
    setValue(String(externalValue ?? ""));
    typed.current = false;
  }, [externalValue]);

  useEffect(() => {
    if (!typed.current) {
      return;
    }
    const timeout = window.setTimeout(() => {
      typed.current = false;
      latest.current(value);
    }, 200);
    return () => window.clearTimeout(timeout);
  }, [value]);

  /** Report at once, for a visitor who submits rather than pausing. */
  const flush = () => {
    if (typed.current) {
      typed.current = false;
      latest.current(value);
    }
  };

  return (
    <input
      aria-label={ariaLabel}
      className={className}
      inputMode={inputMode}
      onBlur={flush}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        typed.current = true;
        setValue(event.target.value);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          flush();
        }
      }}
      placeholder={placeholder ?? "Filter…"}
      type="text"
      value={value}
    />
  );
}

export function textCell(value: string | null): ReactNode {
  return value ?? MISSING_VALUE;
}

/**
 * Render a footer aggregate as a muted label above its value.
 * Stacking keeps narrow metric columns legible without widening the summary row.
 */
export function footerStat(label: string, value: string): ReactNode {
  return (
    <span className="oss-table-footer-stat">
      <span className="oss-table-footer-label">{label}</span>
      <span className="oss-table-footer-value">{value}</span>
    </span>
  );
}

/** Row shape required to render the country cell. */
export interface CountryFields {
  country: string | null;
  country_code: string | null;
}

export function CountryCell<TData extends CountryFields>({
  row,
}: CellContext<TableFeatures, TData, unknown>) {
  const country = row.original.country;
  const flag = countryCodeToFlag(row.original.country_code);
  if (country === null && flag === null) {
    return MISSING_VALUE;
  }
  return (
    <span className="oss-table-country">
      {flag && <span aria-hidden="true">{flag}</span>}
      {country && <span>{country}</span>}
    </span>
  );
}

/**
 * Each country's ISO 3166-1 alpha-2 code, as the rows carry it.
 *
 * The codes ride along on the rows, so nothing needs a list anyone has to
 * maintain, and the pairing is one-to-one in the data.
 */
function countryCodes<TData extends CountryFields>(
  data: readonly TData[],
): Map<string, string> {
  const codes = new Map<string, string>();
  for (const row of data) {
    if (row.country !== null && row.country_code !== null) {
      codes.set(row.country, row.country_code);
    }
  }
  return codes;
}

/**
 * Column meta letting a country be searched and shared by its code.
 *
 * The picker gains `US` and `🇺🇸` for `United States`, and the URL writes
 * `country=US` and reads it back.
 */
export function countryColumnMeta<TData extends CountryFields>(
  data: readonly TData[],
): { facetKeys: (value: unknown) => string[]; urlValue: UrlValueCodec } {
  const codes = countryCodes(data);
  const names = new Map([...codes].map(([name, code]) => [code, name]));
  return {
    facetKeys: (value) => {
      const code = typeof value === "string" ? codes.get(value) : undefined;
      if (code === undefined) {
        return [];
      }
      const flag = countryCodeToFlag(code);
      return flag === null ? [code] : [code, flag];
    },
    urlValue: {
      decode: (text) => names.get(text.toUpperCase()),
      encode: (value) => codes.get(String(value)) ?? String(value),
    },
  };
}

export function LatitudeCell<TData extends RowData>({
  getValue,
}: CellContext<TableFeatures, TData, unknown>) {
  return <CoordinateBadge axis="latitude" value={getValue<number | null>()} />;
}

export function LongitudeCell<TData extends RowData>({
  getValue,
}: CellContext<TableFeatures, TData, unknown>) {
  return <CoordinateBadge axis="longitude" value={getValue<number | null>()} />;
}

/** Share the geographic badges' layout while scaling each axis to its extent. */
export function CoordinateBadge({ axis, value }: {
  axis: "latitude" | "longitude";
  value: number | null;
}) {
  if (value === null) {
    return MISSING_VALUE;
  }
  const latitude = axis === "latitude";
  const direction = latitude
    ? (value >= 0 ? "north" : "south")
    : (value >= 0 ? "east" : "west");
  const letter = latitude
    ? (value >= 0 ? "ℕ" : "𝕊")
    : (value >= 0 ? "𝔼" : "𝕎");
  const shade = 255 - Math.round((Math.abs(value) / (latitude ? 90 : 180)) * 255);
  return (
    <span
      aria-label={`${Math.abs(value).toFixed(1)} degrees${value === 0 ? "" : ` ${direction}`}`}
      className="oss-table-coordinate"
      style={{
        "--oss-coordinate-background": `rgb(${shade}, ${shade}, ${shade})`,
        // Preserve white lettering except on near-white backgrounds.
        color: shade > 224 ? "black" : "white",
      } as CSSProperties}
    >
      <span aria-hidden="true" className="oss-table-hemisphere">
        {value === 0 ? "—" : letter}
      </span>
      <span>{Math.abs(value).toFixed(1)}°</span>
    </span>
  );
}

export function interpolateColor(
  start: [number, number, number],
  end: [number, number, number],
  value: number,
) {
  const amount = Math.min(1, Math.max(0, value));
  return `rgb(${start.map((channel, index) => Math.round(channel + (end[index] - channel) * amount)).join(", ")})`;
}

export function sequentialColor(value: number) {
  return interpolateColor([255, 255, 255], [161, 0, 191], value);
}

/** Underline a numeric cell proportionally to its share of the column maximum. */
export function metricCell<TData extends RowData>(
  maximum: number,
  formatter: (value: number | null) => string = formatNumber,
) {
  return ({ getValue }: CellContext<TableFeatures, TData, unknown>) => {
    const value = getValue<number | null>();
    const style =
      value === null || maximum <= 0
        ? undefined
        : ({ "--oss-metric-color": sequentialColor(value / maximum) } as CSSProperties);
    return (
      <span className="oss-table-metric" style={style}>
        {formatter(value)}
      </span>
    );
  };
}

/** Largest numeric value of `field`, used to scale metric underlines. */
export function columnMaximum<TData>(
  data: readonly TData[],
  field: keyof TData,
): number {
  return Math.max(
    0,
    ...data.flatMap((row) => (typeof row[field] === "number" ? [row[field]] : [])),
  );
}

/** Options rendered at once before asking the visitor to narrow the search. */
const MAX_FACET_OPTIONS = 200;

/** Label a facet value, including the booleans and blanks columns may hold. */
function facetOptionLabel(value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return "(unknown)";
  }
  if (value === true) {
    return "Yes";
  }
  if (value === false) {
    return "No";
  }
  return String(value);
}

/** Width of every filter popover, shared with `.oss-table-facet-popover`. */
const POPOVER_WIDTH = 260;

/**
 * A header button that opens a dismissible panel beneath itself.
 *
 * The panel is positioned as fixed rather than nested in the scroll container,
 * which would otherwise clip it and widen the table's scrollable area.
 * `children` is a function so that a panel — whose contents can be costly to
 * derive from the rows — is built only while it is open.
 */
export function FilterPopover({
  ariaLabel,
  children,
  label,
  buttonClassName = "oss-table-facet-trigger",
}: {
  ariaLabel: string;
  children: () => ReactNode;
  label: string;
  buttonClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ left: number; top: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const reposition = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setAnchor({
        left: Math.min(
          Math.max(8, rect.left),
          window.innerWidth - POPOVER_WIDTH - 8,
        ),
        top: rect.bottom + 4,
      });
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !popoverRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const onReposition = () => reposition();
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open]);

  return (
    <>
      <button
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={ariaLabel}
        className={buttonClassName}
        onClick={() => {
          reposition();
          setOpen((value) => !value);
        }}
        ref={triggerRef}
        // A narrow column truncates its summary, so keep the whole of it reachable.
        title={label}
        type="button"
      >
        {label}
      </button>
      {open && anchor && (
        <div
          aria-label={ariaLabel}
          className="oss-table-facet-popover"
          ref={popoverRef}
          role="dialog"
          style={{ left: anchor.left, top: anchor.top }}
        >
          {children()}
        </div>
      )}
    </>
  );
}

/** Select visible presentation columns, keeping internal fields out of the picker. */
export function ColumnVisibilityPicker<TData extends RowData>({ table }: {
  table: Table<TableFeatures, TData>;
}) {
  return (
    <FilterPopover ariaLabel="Choose visible columns" buttonClassName="oss-table-button" label="Columns">
      {() => (
        <>
          <div className="oss-table-facet-actions">
            <strong>Visible columns</strong>
            <button onClick={() => table.resetColumnVisibility()} type="button">Reset columns</button>
          </div>
          <div className="oss-table-column-list">
            {table.getAllColumns().map((group) => (
              <fieldset key={group.id}>
                {columnLabel(group.columnDef) && <legend>{columnLabel(group.columnDef)}</legend>}
                {group.getLeafColumns().filter((column) => columnLabel(column.columnDef)).map((column) => (
                  <label key={column.id}>
                    <input
                      checked={column.getIsVisible()}
                      disabled={!column.getCanHide()}
                      onChange={column.getToggleVisibilityHandler()}
                      type="checkbox"
                    />
                    {columnLabel(column.columnDef)}
                  </label>
                ))}
              </fieldset>
            ))}
          </div>
        </>
      )}
    </FilterPopover>
  );
}

/** Make filters and sorting on hidden columns discoverable without clearing them. */
export function HiddenColumnNotices<TData extends RowData>({ table }: {
  table: Table<TableFeatures, TData>;
}) {
  const active = table.getAllLeafColumns().filter((column) =>
    columnLabel(column.columnDef) && !column.getIsVisible() &&
    (column.getIsFiltered() || column.getIsSorted()),
  );
  if (active.length === 0) return null;
  return (
    <div className="oss-table-hidden-columns">
      {active.map((column) => (
        <button className="oss-table-button" key={column.id} onClick={() => column.toggleVisibility(true)} type="button">
          {columnLabel(column.columnDef)} ({[
            column.getIsFiltered() && "filtered",
            column.getIsSorted() && "sorted",
          ].filter(Boolean).join(", ")}) · Show column
        </button>
      ))}
    </div>
  );
}

/**
 * Multi-select filter listing a column's distinct values with their counts.
 *
 * TanStack computes the counts from rows passing every *other* column's
 * filters, so the options always describe what is still reachable.
 */
export function FacetedFilter<TData extends RowData>({
  ariaLabel,
  column,
}: {
  ariaLabel: string;
  column: Column<TableFeatures, TData, unknown>;
}) {
  const selected = (column.getFilterValue() as unknown[] | undefined) ?? [];
  return (
    <FilterPopover
      ariaLabel={ariaLabel}
      label={
        selected.length === 0 ? "Any" : `${formatNumber(selected.length)} selected`
      }
    >
      {() => <FacetedFilterPanel ariaLabel={ariaLabel} column={column} />}
    </FilterPopover>
  );
}

function FacetedFilterPanel<TData extends RowData>({
  ariaLabel,
  column,
}: {
  ariaLabel: string;
  column: Column<TableFeatures, TData, unknown>;
}) {
  const [query, setQuery] = useState("");

  const selected = (column.getFilterValue() as unknown[] | undefined) ?? [];
  const selectedSet = new Set(selected);
  const facets = column.getFacetedUniqueValues();

  // Counts rank a category's long tail usefully, but an identity column such
  // as a name is easier to scan alphabetically.
  const byLabel = column.columnDef.meta?.facetSort === "label";
  const facetKeys = column.columnDef.meta?.facetKeys;
  const options = useMemo(
    () =>
      [...facets.entries()]
        .map(([value, count]) => {
          const label = facetOptionLabel(value);
          return {
            count,
            keys: [label, ...(facetKeys?.(value) ?? [])].map(searchKey),
            label,
            value,
          };
        })
        .sort((a, b) =>
          byLabel
            ? a.label.localeCompare(b.label)
            : b.count - a.count || a.label.localeCompare(b.label),
        ),
    [byLabel, facetKeys, facets],
  );
  const needle = searchKey(query);
  const matches = needle
    ? options.filter((option) => option.keys.some((key) => key.includes(needle)))
    : options;
  const shown = matches.slice(0, MAX_FACET_OPTIONS);
  const showCounts = options.some((option) => option.count > 1);

  const setSelection = (values: unknown[]) =>
    column.setFilterValue(values.length === 0 ? undefined : values);

  return (
    <>
      <input
        aria-label={`Search ${ariaLabel}`}
        autoFocus
        className="oss-table-facet-search"
        onChange={(event: ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
        placeholder="Search values…"
        type="text"
        value={query}
      />
      <div className="oss-table-facet-actions">
        <button
          disabled={matches.length === 0}
          onClick={() =>
            setSelection([
              ...new Set([...selected, ...matches.map((option) => option.value)]),
            ])
          }
          type="button"
        >
          Select {formatNumber(matches.length)}
        </button>
        <button
          disabled={selected.length === 0}
          onClick={() => setSelection([])}
          type="button"
        >
          Clear
        </button>
      </div>
      <div className="oss-table-facet-list">
        {shown.length === 0 ? (
          <p className="oss-table-facet-empty">No matching values.</p>
        ) : (
          shown.map(({ count, label, value }) => (
            <label className="oss-table-facet-option" key={label}>
              <input
                checked={selectedSet.has(value)}
                onChange={() =>
                  setSelection(
                    selectedSet.has(value)
                      ? selected.filter((entry) => entry !== value)
                      : [...selected, value],
                  )
                }
                type="checkbox"
              />
              <span className="oss-table-facet-value">{label}</span>
              {showCounts && (
                <span className="oss-table-facet-count">{formatNumber(count)}</span>
              )}
            </label>
          ))
        )}
      </div>
      {matches.length > shown.length && (
        <p className="oss-table-facet-more">
          Showing {formatNumber(shown.length)} of {formatNumber(matches.length)}.
          Keep typing to narrow.
        </p>
      )}
    </>
  );
}

/** The bounds a range column's filter holds, or none. */
function boundsOf(filterValue: unknown): NumericRange {
  return isNumericRange(filterValue) ? filterValue : UNBOUNDED;
}

/** Drawn size of a distribution, in the histogram's own coordinate space. */
const HISTOGRAM_WIDTH = 236;
const HISTOGRAM_HEIGHT = 54;

/**
 * Read a column's values in the units its filter box speaks.
 *
 * The rows are those passing every *other* column's filters, so the
 * distribution describes what is still reachable and holds still while its own
 * bounds are dragged.
 */
function filterValues<TData extends RowData>(
  column: Column<TableFeatures, TData, unknown>,
  rows: readonly Row<TableFeatures, TData>[],
): (number | null)[] {
  const scale = column.columnDef.meta?.filterScale ?? 1;
  return rows.map((row) => {
    const value = row.getValue(column.id);
    return typeof value === "number" ? value * scale : null;
  });
}

/**
 * A column's distribution, with the bars inside the current bounds picked out.
 *
 * Bar heights use a square-root scale: ski-area metrics are long-tailed enough
 * that a linear one leaves every bar but the first invisible.
 */
function RangeHistogram({
  bounds,
  format,
  histogram,
  integer,
  onSelect,
}: {
  bounds: NumericRange;
  format: (value: number) => string;
  histogram: Histogram;
  integer: boolean;
  onSelect: (bounds: NumericRange, settled: boolean) => void;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const anchorRef = useRef<number | null>(null);
  const binWidth = HISTOGRAM_WIDTH / histogram.bins.length;

  const binAt = (clientX: number) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) {
      return 0;
    }
    const ratio = (clientX - rect.left) / rect.width;
    return Math.min(
      histogram.bins.length - 1,
      Math.max(0, Math.floor(ratio * histogram.bins.length)),
    );
  };

  const select = (clientX: number, settled: boolean) => {
    const anchor = anchorRef.current;
    if (anchor === null) {
      return;
    }
    onSelect(boundsFromBins(histogram, anchor, binAt(clientX), integer), settled);
  };

  return (
    <svg
      aria-label={`Distribution from ${format(histogram.start)} to ${format(histogram.end)}. Drag across the bars to select a range.`}
      className="oss-table-range-plot"
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        anchorRef.current = binAt(event.clientX);
        select(event.clientX, false);
      }}
      onPointerMove={(event) => {
        if (anchorRef.current !== null) {
          select(event.clientX, false);
        }
      }}
      onPointerUp={(event) => {
        select(event.clientX, true);
        anchorRef.current = null;
      }}
      preserveAspectRatio="none"
      ref={svgRef}
      role="img"
      viewBox={`0 0 ${HISTOGRAM_WIDTH} ${HISTOGRAM_HEIGHT}`}
    >
      {histogram.bins.map((bin, index) => {
        const height =
          bin.count === 0
            ? 0
            : Math.max(
                1,
                Math.sqrt(bin.count / histogram.maxCount) * HISTOGRAM_HEIGHT,
              );
        const selected = overlapsBin(bounds, histogram, index, integer);
        return (
          <rect
            className={
              selected ? "oss-table-range-bar" : "oss-table-range-bar-excluded"
            }
            height={Math.max(height, 0.75)}
            key={bin.start}
            width={Math.max(binWidth - 1, 0.75)}
            x={((bin.start - histogram.start) / (histogram.end - histogram.start)) * HISTOGRAM_WIDTH}
            y={HISTOGRAM_HEIGHT - height}
          >
            <title>
              {describeBin(histogram, index, integer, format)}:{" "}
              {formatNumber(bin.count)}
            </title>
          </rect>
        );
      })}
    </svg>
  );
}

function RangeFilterPanel<TData extends RowData>({
  ariaLabel,
  column,
}: {
  ariaLabel: string;
  column: Column<TableFeatures, TData, unknown>;
}) {
  const rows = column.getFacetedRowModel().flatRows;
  const values = useMemo(() => filterValues(column, rows), [column, rows]);
  const histogram = useMemo(() => buildHistogram(values), [values]);
  const filterValue = column.getFilterValue();
  const [dragged, setDragged] = useState<NumericRange | null>(null);
  const bounds = dragged ?? boundsOf(filterValue);

  // The callbacks depend on the column alone, so the debounced boxes below see
  // an identity that settles instead of churning.
  const commit = useCallback(
    (next: NumericRange) => {
      setDragged(null);
      column.setFilterValue(restricts(next) ? next : undefined);
    },
    [column],
  );
  const commitBound = useCallback(
    (edge: "lower" | "upper", text: string) => {
      const parsed = parseBound(edge, text);
      if (parsed === null) {
        return;
      }
      // Read the filter as it stands rather than as this render saw it, so
      // editing one bound cannot revert the other.
      commit(withBound(boundsOf(column.getFilterValue()), edge, parsed));
    },
    [column, commit],
  );
  const commitLower = useCallback(
    (text: string) => commitBound("lower", text),
    [commitBound],
  );
  const commitUpper = useCallback(
    (text: string) => commitBound("upper", text),
    [commitBound],
  );

  if (histogram === null) {
    return <p className="oss-table-facet-empty">No values to filter.</p>;
  }

  const format = (value: number) =>
    column.columnDef.meta?.filterFormat?.(value) ??
    formatBound(value, histogram.precision);
  const integer = column.columnDef.meta?.integer ?? false;
  // The boxes show a bound exactly as it is applied, however it was typed;
  // only the axis labels round to the bin precision.
  const boundText = (value: number) => (Number.isFinite(value) ? String(value) : "");
  const keptCount = values.filter(
    (value) => value !== null && rangeContains(bounds, value),
  ).length;
  // Name only the end the axis actually stops short of.
  const outliers = [
    histogram.start > histogram.minimum
      ? `down to ${format(histogram.minimum)}`
      : null,
    histogram.end < histogram.maximum ? `up to ${format(histogram.maximum)}` : null,
  ]
    .filter((clause) => clause !== null)
    .join(" and ");

  return (
    <>
      <p className="oss-table-range-summary">
        {formatNumber(keptCount)} of {formatNumber(histogram.valueCount)} in range
        {histogram.missingCount > 0 &&
          ` · ${formatNumber(histogram.missingCount)} missing`}
      </p>
      <RangeHistogram
        bounds={bounds}
        format={format}
        histogram={histogram}
        integer={integer}
        onSelect={(next, settled) => (settled ? commit(next) : setDragged(next))}
      />
      <div className="oss-table-range-axis">
        <span>{format(histogram.start)}</span>
        <span>{format(histogram.end)}</span>
      </div>
      {outliers !== "" && (
        <p className="oss-table-facet-more">End bars hold outliers {outliers}.</p>
      )}
      <div className="oss-table-range-inputs">
        <DebouncedInput
          ariaLabel={`Minimum ${ariaLabel}`}
          className="oss-table-range-input"
          inputMode="decimal"
          onChange={commitLower}
          placeholder={boundText(histogram.start)}
          value={boundText(bounds.lower)}
        />
        <span aria-hidden="true">to</span>
        <DebouncedInput
          ariaLabel={`Maximum ${ariaLabel}`}
          className="oss-table-range-input"
          inputMode="decimal"
          onChange={commitUpper}
          placeholder={boundText(histogram.end)}
          value={boundText(bounds.upper)}
        />
      </div>
      <div className="oss-table-facet-actions">
        <span className="oss-table-facet-more">Drag the bars to select.</span>
        <button
          disabled={filterValue === undefined}
          onClick={() => commit(UNBOUNDED)}
          type="button"
        >
          Clear
        </button>
      </div>
    </>
  );
}

/** Filter a numeric column by brushing or typing bounds over its distribution. */
export function RangeFilter<TData extends RowData>({
  ariaLabel,
  column,
}: {
  ariaLabel: string;
  column: Column<TableFeatures, TData, unknown>;
}) {
  const bounds = boundsOf(column.getFilterValue());
  const format = column.columnDef.meta?.filterFormat ?? formatBound;
  return (
    <FilterPopover ariaLabel={ariaLabel} label={describeBounds(bounds, format)}>
      {() => <RangeFilterPanel ariaLabel={ariaLabel} column={column} />}
    </FilterPopover>
  );
}

/** The filter control a column's `filterVariant` asks for. */
export function ColumnFilter<TData extends RowData>({
  column,
}: {
  column: Column<TableFeatures, TData, unknown>;
}) {
  const ariaLabel = `Filter ${column.id}`;
  return column.columnDef.meta?.filterVariant === "faceted" ? (
    <FacetedFilter ariaLabel={ariaLabel} column={column} />
  ) : (
    <RangeFilter ariaLabel={ariaLabel} column={column} />
  );
}
